import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  effect,
  signal
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup
} from '@angular/forms';
import { Debounce, Throttle } from 'lodash-decorators';
import { EMPTY, interval, merge, of } from 'rxjs';
import {
  auditTime,
  catchError,
  filter,
  finalize,
  map,
  startWith,
  switchMap
} from 'rxjs/operators';

import {
  API_PREFIX_V2,
  GlobalWebsocketService
} from '@approot/global-websocket';
import {
  RFQ_QUOTE_CONFIRM_RFE_ACCEPT,
  RFQ_QUOTE_CONFIRM_RFE_CLIENT_ACCEPT,
  RfqBidOfferQuotes,
  RfqQuote,
  RfqTabLegs,
  RfqTabPosition
} from '@approot/models';
import {
  AlertsService,
  ConfigService,
  ExecutionContext,
  ExecutionContextType,
  PhoenixService,
  ProductsConfigService,
  RfqDetails,
  RfqService,
  RfqTabService,
  VolBasedRfqOptionPriceV2Result
} from '@approot/services';
import {
  fadeSlowAnimation,
  pulseQuoteAnimation,
  pulseQuoteButtonAnimation
} from '@approot/shared';
import { valueIncreaseDecrease } from '@approot/shared/animations';
import { mungeQuotes } from '@approot/shared/custom-components/rfq-quotes/quotes';
import {
  SubscriptionSink,
  Utilities,
  clone,
  deepFreeze,
  exponentialDelay,
  formatPremium,
  pipelineBindAll,
  shortUuid,
  sleep
} from '@approot/utilities';
import { components } from '@approot/utilities/digital-vega-schema';
import { LegComponent } from '@approot/widgets';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import { ProviderQuote, ProviderQuoteEvent } from '../provider-quote.model';
import { ProviderQuoteService, TabItem } from '../provider-quote.service';
import {
  calculatePremiumAmount,
  calculatePremiumBasedRisks,
  calculatePremiumInStyle,
  calculateVolBasedRisks,
  createVolsFormArray,
  getPricePayload,
  legPremiumAmountBid,
  legPremiumAmountOffer,
  premiumInStyleCurrentlyQuoted,
  setCashPremiums,
  setVendorOptionPremium,
  updatePremiumInStyleValue
} from './funcs';
import {
  Action,
  ActionMessage,
  ActiveClass,
  AllPremiumBasedRisksRecords,
  AllVolBasedRisksRecords,
  CashPremiumInfo,
  ClientPriceDisplayResults,
  ClientPriceResults,
  DeadStatuses,
  DeltaType,
  FormDef,
  FormKey,
  Hedge,
  LegInfo,
  LegResult,
  Premium,
  PremiumCash,
  PremiumInStyleCurrentlyQuoted,
  PremiumInStyleInfo,
  PriceResult,
  PriceStreamingState,
  QuoteStatus,
  ReceiveOrPay,
  Vega,
  VolsError
} from './provider-quote-types';

const SingleVolProducts = ['vanilla', 'ndo', 'straddle'];
const SimpleLegTypes = [...SingleVolProducts, 'strangle'];
const { cleanFormattedNumeric, minmax, formatNumber, percentageToNumber } =
  Utilities;

type QuoteSubmitStatus = 'INITIAL' | 'SUBMITTING' | 'ERROR' | 'SUCCESS';
type QuoteSubmit = {
  firstSuccess: boolean;
  quoteSubmitStatus: QuoteSubmitStatus;
};

const initialQuoteSubmit: QuoteSubmit = deepFreeze({
  firstSuccess: false,
  quoteSubmitStatus: 'INITIAL'
});

@Component({
  selector: 'app-provider-quote-prices',
  templateUrl: './provider-quote-prices.component.html',
  styleUrls: ['./provider-quote-prices.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    pulseQuoteAnimation,
    fadeSlowAnimation,
    pulseQuoteButtonAnimation,
    valueIncreaseDecrease
  ]
})
export class ProviderQuotePricesComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() public rfqDetails: RfqDetails;
  @Input() public quoteEvent: ProviderQuoteEvent;
  @Input() public legs: RfqTabLegs[];
  @Input() public volBasedRfqOptionPriceResult: VolBasedRfqOptionPriceV2Result;
  @Input() public pricesLoading = false;
  @Input() public initialPrice = true;
  @Input() public setupForm: FormGroup;
  @Input() public executionContext: ExecutionContext;
  @Input() public isPremiumBasedVanillasWithSpotFeed = false;
  @Input() public isVolBased = false;
  @Input() public forwardPointsMultiplier: number;
  @Input() public pipsHighlightedIndices: number[] = [];
  @Output() public closeTab = new EventEmitter();
  @Output() public quoteEventActionChange = new EventEmitter<Action>();
  public executionType: ExecutionContextType;
  public legInfo: LegInfo[] = [];
  public timeLimit = Date.now();
  public action: Action;
  public quoteStatus: QuoteStatus;
  public actionMessage: string;
  public isOwner = false;
  public form: FormGroup;
  public premiumCCY: string;
  public baseCCY: string;
  public pickupErrorMessage: string;
  public expiresIn = 0;
  public hedgeRate: number;
  public hedge: Hedge;
  public spotRate: number;
  public spotDelta: number;
  public vega: Vega;
  public priceResult: PriceResult;
  public timeSincePrice: number;
  public hasVols: boolean;
  public canQuote: boolean;
  public canWithdraw: boolean;
  public canRelease: boolean;
  public canAccept: boolean;
  public canDeclineRFQ: boolean;
  public canDeclineRFE: boolean;
  public quoteInFlight: boolean;
  public isExpired: boolean;
  public isComplete: boolean;
  public isTraded: boolean;
  public activeQuote: ProviderQuote;
  public enableAutoQuote = true;
  public bidActiveClass: ActiveClass;
  public offerActiveClass: ActiveClass;
  public impliedMarginBid: number;
  public impliedMarginOffer: number;
  public isCustomHedgeRate: boolean;
  public priceStreamingState: PriceStreamingState;
  public showMargin = true;
  public showDvIndic = true;
  public showVendorPrice = true;
  public showSpotRate = true;
  public showStreamingButton = true;
  public showImpliedMargin = true;
  public formValid = false;
  public setupFormValid = false;
  public volsError: VolsError = null;
  public currentClientPrice: ClientPriceDisplayResults;
  public risks: AllVolBasedRisksRecords & AllPremiumBasedRisksRecords;
  public ccyPair: string;
  public clientActionsHeaders: RfqTabPosition = {
    bid: 'Client Sells',
    offer: 'Client Buys'
  };
  public termPair: string;
  public showInlineVol = false;
  public legPositions: ('Buy' | 'Sell')[] = [];
  public currentlyQuotingClientPos: { bid: string; offer: string };
  public quoteSubmit = signal<Partial<QuoteSubmit>>(initialQuoteSubmit);
  public accepting: boolean;
  public isWhiteLabel = false;
  public bankToFilter: string;
  public premiumInStyleInfo: PremiumInStyleInfo = {
    label: { bid: null, offer: null },
    format: { bid: null, offer: null },
    liveValue: { bid: true, offer: true },
    latestValue: { bid: null, offer: null }
  };
  public premiumInStyleCurrentlyQuoted: PremiumInStyleCurrentlyQuoted;
  public cashPremiumInfo: CashPremiumInfo = {
    label: { bid: null, offer: null },
    direction: { bid: null, offer: null },
    liveValue: { bid: true, offer: true },
    latestValue: { bid: null, offer: null }
  };
  private subsink = new SubscriptionSink();
  private autoQuoteTimer: any;
  private autoQuoteTime: number;
  private streamingWebsocketUUID = 'stream_' + shortUuid();
  private manualWebsocketUUID = 'manual_' + shortUuid();
  private lastPhxLogEvent: any = null;
  private canQuotePoller: any = null;

  constructor(
    private rfqService: RfqService,
    private rfqTabService: RfqTabService,
    private changeRef: ChangeDetectorRef,
    private quoteService: ProviderQuoteService,
    private fb: FormBuilder,
    private webSocketService: GlobalWebsocketService,
    private productsConfigService: ProductsConfigService,
    private phxService: PhoenixService,
    private alertsService: AlertsService,
    private configService: ConfigService
  ) {
    this.form = new FormGroup({
      premiumAmountOffer: new FormControl(''),
      premiumAmountBid: new FormControl(''),
      volBid: new FormArray([new FormControl('')]),
      volOffer: new FormArray([new FormControl('')]),
      hedgeRateBid: new FormControl(''),
      hedgeRateOffer: new FormControl(''),
      hedgeBid: new FormControl(''),
      hedgeOffer: new FormControl(''),
      bidOption: new FormControl('clientPays'),
      offerOption: new FormControl('clientReceives'),
      bidPremiumInStyle: new FormControl(''),
      offerPremiumInStyle: new FormControl(''),
      hedgeBidOption: new FormControl(''),
      hedgeOfferOption: new FormControl(''),
      marginBid: new FormControl('0'),
      marginOffer: new FormControl('0'),
      cashPremiumBid: new FormControl(''),
      cashPremiumOffer: new FormControl('')
    } as FormDef);

    effect(() => {
      const state = this.quoteSubmit();
      this.phxService.logEvent({
        type: 'medusa',
        category: 'submitProviderQuote',
        urlPath: '/rfq/submitProviderQuote',
        state
      });
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.legs) {
      this.legs = clone(changes.legs.currentValue);
    }

    if (changes.volBasedRfqOptionPriceResult?.currentValue) {
      this.setCashPremiums();
      this.updatePremiumInStyleValues();
      if (this.isPremiumBased) {
        this.risks = calculatePremiumBasedRisks(
          this.volBasedRfqOptionPriceResult
        );
      }
    }

    if (changes.pricesLoading && this.pricesLoading === false) {
      this.resubmitQuote();
    }

    if (changes.rfqDetails?.currentValue) {
      this.isWhiteLabel = this.rfqDetails.is_wl;
      this.bankToFilter = this.rfqDetails.clientProvider?.shortName;
    }
  }

  public ngOnDestroy(): void {
    this.subsink.unsubscribe();
    this.cancelAutoQuoting();
    this.webSocketService.unsubscribe(this.streamingWebsocketUUID);
    this.webSocketService.unsubscribe(this.manualWebsocketUUID);
    clearInterval(this.canQuotePoller);
  }

  public ngOnInit(): void {
    const requestId = this.quoteEvent.quote.request.requestId;
    this.subscribeToEvents(requestId);
    const action = this.quoteEvent.action;
    if (action !== 'CANCELLED' && action !== 'Declined') {
      if (this.currentUser === this.quoteEvent.quote.owner) {
        this.isOwner = true;
        this.postPickup();
      } else {
        this.attemptPickup();
      }
    }

    this.enableAutoQuote = true; // this.quoteService.getAutoQuoteState();

    if (!this.isPremiumBasedVanillasWithSpotFeed) {
      this.initPriceListener();
    }

    this.setPriceStreamingState('STREAMING');
    this.setExecutionContextData();
    this.subscribeToValueChanges();
    this.subscribeToClientPrice(requestId);
    this.pollForLegPositions();

    this.canQuotePoller = setInterval(() => {
      this.logCanQuote(this.quoteEvent);
      // if (!this.getCanQuote()) {
      // }
    }, 2000);
  }

  public get isPremiumBased(): boolean {
    return !this.isVolBased;
  }

  public formatPremium = formatPremium;

  private subscribeToValueChanges() {
    this.subsink.sink = this.form.valueChanges
      .pipe(auditTime(500))
      .subscribe(() => this.updateState());

    this.subsink.sink = this.setupForm.statusChanges
      .pipe(startWith(this.setupForm.status))
      .subscribe(_ => {
        this.setupFormValid = this.setupValid();
      });
  }

  public setupValid(): boolean {
    const setupValue = this.setupForm.value;
    return this.setupForm.valid && setupValue.hedges.every(y => y.notional);
  }

  private updateState() {
    this.volsError = null;
    this.legs.every((_, i) => {
      const legInfo = this.legInfo[i];
      if (!legInfo.isSingleVolProduct) {
        return true;
      }
      const bid = this.numericFormArrayValue('volBid', i);
      const offer = this.numericFormArrayValue('volOffer', i);
      if (bid == null || offer == null) {
        // still loading
        return true;
      }
      const inverted = legInfo.inverted;
      if (!inverted) {
        const ok = bid <= offer;
        if (!ok) {
          this.volsError = {
            direction: '>',
            legIndex: i + 1
          };
        }
        return ok;
      } else {
        const ok = bid >= offer;
        if (!ok) {
          this.volsError = {
            direction: '<',
            legIndex: i + 1
          };
        }
        return ok;
      }
    });

    this.formValid = this.volsError == null && this.form.valid;
  }

  private setExecutionContextData(): void {
    this.executionType = this.executionContext.executionTypes[0].shortName;
    if (this.isVolBased || this.isPremiumBasedVanillasWithSpotFeed) {
      this.showMargin = false;
      this.showDvIndic = false;
      this.showImpliedMargin = false;

      if (this.isVolBased) {
        this.showVendorPrice = false;
      }

      if (this.isPremiumBasedVanillasWithSpotFeed) {
        this.showSpotRate = false;
        this.showStreamingButton = false;
      }
    }

    if (!this.isVolBased) {
      this.showInlineVol = this.legs.length <= 2;
    }
  }

  public doQuote() {
    this.cancelAutoQuoting();
    if (!this.form.valid || !this.setupValid()) {
      return;
    }

    this.activeQuote = pipelineBindAll(this)(
      clone(this.quoteEvent.quote, true),
      this.processLegs,
      this.processHedge,
      this.processPremiums,
      this.submitQuote
    );

    this.requote();
  }

  public doDeclineRFQ() {
    this.quoteSubmit.set(initialQuoteSubmit);
    this.rfqService
      .decline({ providerQuote: this.quoteEvent.quote })
      .subscribe({
        error: err => console.error('doDeclineRFQ', err)
      });
  }

  public doDeclineRFE() {
    this.rfqService.declineRFE({ quote: this.quoteEvent.quote }).subscribe({
      error: err => console.error('doDeclineRFE', err)
    });
  }

  public doWithdraw() {
    this.quoteSubmit.set(initialQuoteSubmit);

    this.rfqService.withdraw({ quote: this.quoteEvent.quote }).subscribe({
      error: err => console.error('doWithdraw', err)
    });
  }

  public doRelease() {
    this.quoteSubmit.set(initialQuoteSubmit);
    this.rfqService
      .releaseQuote({ providerQuote: this.quoteEvent.quote })
      .subscribe({
        error: err => console.error('doRelease', err)
      });
  }

  public doAccept(iter = 0) {
    const logCorrelationId = shortUuid();
    const urlPath = '/rfq/confirmAcceptance';

    this.phxService.logEvent({
      id: logCorrelationId,
      type: 'medusa',
      category: 'providerQuoteAccept',
      urlPath,
      message: `Accepting quote (try  ${iter + 1})`
    });
    this.quoteSubmit.set(initialQuoteSubmit);
    this.accepting = true;
    const payload = { providerQuote: this.quoteEvent.quote };
    this.rfqService.confirmAcceptance(payload).subscribe({
      next: async r => {
        if (r.result === true) {
          this.accepting = false;
          return;
        }
        if (iter >= 5) {
          const message = `Error accepting quote (try ${iter + 1})`;
          this.alertsService.addAlert({
            message,
            urlPath,
            payload,
            error: r
          });
          this.phxService.logEvent({
            logCorrelationId,
            type: 'medusa',
            category: 'providerQuoteAccept',
            urlPath,
            message,
            payload
          });
          this.accepting = false;
        } else {
          await sleep(500);
          this.doAccept(iter + 1);
        }
      },
      error: err => {
        this.alertsService.addAlert({
          message: 'Error accepting quote.',
          urlPath,
          payload,
          error: err
        });
        this.phxService.logEvent({
          logCorrelationId,
          type: 'medusa',
          category: 'providerQuoteAccept',
          urlPath,
          message: `Error accepting quote`,
          error: err
        });
        console.error('doAccept', err);
        this.accepting = false;
      }
    });
  }

  public get currentUser(): string {
    return this.rfqService.currentUserId;
  }

  public syncSides() {
    const syncBidOffer = (name: string) => {
      const bidCtrl = this.formCtrl(`${name}Bid` as FormKey);
      const bid = bidCtrl.value;
      const offerCtrl = this.formCtrl(`${name}Offer` as FormKey);
      const offer = offerCtrl.value;
      if (bid && !offer) {
        offerCtrl.setValue(bid);
      } else if (offer && !bid) {
        bidCtrl.setValue(offer);
      }
      this.form.updateValueAndValidity();
    };

    syncBidOffer('hedge');
    syncBidOffer('hedgeRate');
    syncBidOffer('margin');
    this.setCashPremiums();
  }

  public formCtrl(key: FormKey): AbstractControl {
    return this.form.get(key);
  }

  public formValue(key: FormKey): string {
    return this.formCtrl(key)?.value as string;
  }

  public formArrayValue(key: FormKey, idx: number): string {
    return this.formCtrl(key)?.value[idx] as string;
  }

  public numericFormValue(key: FormKey): number {
    const num = this.formCtrl(key)?.value as string;
    return cleanFormattedNumeric(num);
  }

  public numericFormArrayValue(key: FormKey, idx: number): number {
    const num = this.formCtrl(key)?.value[idx] as string;
    return cleanFormattedNumeric(num);
  }

  public toggleAutoQuote() {
    this.enableAutoQuote = !this.enableAutoQuote;
    this.quoteService.setAutoQuoteState(this.enableAutoQuote);
    if (this.enableAutoQuote) {
      this.requote();
    }
  }

  public setPriceStreamingState(state: PriceStreamingState) {
    this.priceStreamingState = state;
    if (state === 'STREAMING') {
      const editable: FormKey[] = ['marginBid', 'marginOffer'];
      Object.entries(this.form.controls).forEach(
        ([name, ctrl]: [FormKey, AbstractControl]) => {
          if (!editable.includes(name)) {
            ctrl.disable();
          }
        }
      );
    } else {
      this.form.enable();
    }
  }

  public priceManual() {
    this.pricesLoading = true;
    this.setPriceStreamingState('MANUAL');
    const payload = getPricePayload(
      this.legs,
      this.premiumCCY,
      this.executionType,
      this.rfqDetails
    );
    this.webSocketService.sendMessage(
      {
        action: `${API_PREFIX_V2}pricing`,
        payload,
        uuid: this.manualWebsocketUUID
      },
      { subscribeOnReconnect: true }
    );

    this.changeRef.markForCheck();
  }

  public autoQuoteTimeDeltaSecs() {
    return Math.floor((this.autoQuoteTime - Date.now()) / 1000);
  }

  public getTradeDirection(): 'Buy' | 'Sell' | 'Unknown' {
    const quote = this.quoteEvent.quote;
    return quote?.tradeDirection;
  }

  @Throttle(250)
  public get quotes(): RfqBidOfferQuotes {
    return mungeQuotes(this.rfqTabService.quotesBeingDisplayed);
  }

  @Throttle(250)
  public get currentTimestamp(): number {
    return this.rfqService.currentTimestamp;
  }

  @Throttle(250)
  public get allQuotes(): RfqQuote[] {
    return [
      ...this.rfqTabService.quotesBeingDisplayed.bids,
      ...this.rfqTabService.quotesBeingDisplayed.offers
    ];
  }

  public blurPremiumInStyle(side: 'bid' | 'offer', value: string): void {
    if (!value) {
      const latestValueCashPremium = this.cashPremiumInfo.latestValue[side];
      const latestValuePremiumInStyle = Math.abs(
        +this.premiumInStyleInfo.latestValue[side]
      );
      if (!this.premiumInStyleInfo.liveValue[side]) {
        this.formCtrl(
          side === 'bid' ? 'bidPremiumInStyle' : 'offerPremiumInStyle'
        ).setValue(latestValuePremiumInStyle);

        this.formCtrl(
          side === 'bid' ? 'cashPremiumBid' : 'cashPremiumOffer'
        ).setValue(latestValueCashPremium);
      }
      this.premiumInStyleInfo.liveValue[side] = true;
      this.cashPremiumInfo.liveValue[side] = true;
    }
  }

  public changeInPremiumInStyle(side: 'bid' | 'offer', value: string): void {
    const v = +value;
    if (v < 0) {
      return;
    }
    const premiumAmount = calculatePremiumAmount(
      this.volBasedRfqOptionPriceResult,
      value
    );
    const formattedValue = formatNumber(premiumAmount, 2);
    this.formCtrl(
      side === 'bid' ? 'cashPremiumBid' : 'cashPremiumOffer'
    ).setValue(formattedValue);
    this.premiumInStyleInfo.liveValue[side] = false;
    this.cashPremiumInfo.liveValue[side] = false;
    this.resubmitQuoteOnInput();
  }

  public blurCashPremium(side: 'bid' | 'offer', value: string): void {
    if (!value) {
      const latestValueCash = this.cashPremiumInfo.latestValue[side];
      const latestValuePremiumInStyle = Math.abs(
        +this.premiumInStyleInfo.latestValue[side]
      );
      if (!this.cashPremiumInfo.liveValue[side]) {
        this.formCtrl(
          side === 'bid' ? 'cashPremiumBid' : 'cashPremiumOffer'
        ).setValue(latestValueCash);

        this.formCtrl(
          side === 'bid' ? 'bidPremiumInStyle' : 'offerPremiumInStyle'
        ).setValue(latestValuePremiumInStyle);
      }
      this.cashPremiumInfo.liveValue[side] = true;
      this.premiumInStyleInfo.liveValue[side] = true;
    }
  }

  public changeInCashPremium(side: 'bid' | 'offer', value: string): void {
    const premiumAmount = calculatePremiumInStyle(
      side === 'bid' ? 'bid' : 'ask',
      this.volBasedRfqOptionPriceResult,
      value
    );
    const formattedValue = updatePremiumInStyleValue(
      this.premiumInStyleInfo.format[side],
      premiumAmount.value
    );
    const ctrl = this.form.get(
      side === 'bid' ? 'bidPremiumInStyle' : 'offerPremiumInStyle'
    );
    ctrl.setValue(formattedValue);
    this.cashPremiumInfo.liveValue[side] = false;
    this.premiumInStyleInfo.liveValue[side] = false;
    this.resubmitQuoteOnInput();
  }

  /*<PRIVATE>*********************************************************/

  @Debounce(1000)
  private resubmitQuoteOnInput(): void {
    this.resubmitQuote();
  }

  private resubmitQuote(): void {
    if (
      this.quoteInFlight &&
      this.priceStreamingState === 'STREAMING' &&
      this.isPremiumBased
    ) {
      console.log('Requote after price update');
      this.doQuote();
    }
  }

  private startStreamingPricing() {
    if (this.isPremiumBasedVanillasWithSpotFeed) {
      return;
    }
    this.pricesLoading = true;
    const payload = getPricePayload(
      this.legs,
      this.premiumCCY,
      this.executionType,
      this.rfqDetails
    );
    this.webSocketService.sendMessage(
      {
        action: `${API_PREFIX_V2}priceStream`,
        payload,
        uuid: this.streamingWebsocketUUID
      },
      { subscribeOnReconnect: true }
    );

    this.changeRef.markForCheck();
  }

  private initPriceListener(): void {
    const resubscribe = (iteration: number) => {
      this.subsink.sink = merge(
        this.webSocketService.getListener<PriceResult>({
          action: `${API_PREFIX_V2}priceStream`,
          uuid: this.streamingWebsocketUUID,
          take: Infinity
        }),
        this.webSocketService.getListener<PriceResult>({
          action: `${API_PREFIX_V2}pricing`,
          uuid: this.manualWebsocketUUID,
          take: Infinity
        })
      )
        .pipe(
          auditTime(1_000),
          catchError(err => {
            console.error('initPriceListener', err);
            return EMPTY;
          }),
          switchMap(r => {
            this.pricesLoading = false;
            if (!r || r.error) {
              return EMPTY;
            }
            return of(r);
          }),
          filter(r => {
            if (this.isTraded) {
              return false;
            } else if (this.priceStreamingState === 'MANUAL') {
              return r.uuid === this.manualWebsocketUUID;
            } else if (this.priceStreamingState === 'STREAMING') {
              return r.uuid === this.streamingWebsocketUUID;
            } else {
              return false;
            }
          })
        )
        .subscribe({
          next: r => {
            this.processPricingResult(r.payload || r.body);
            // switch to Manual mode after the 1st price in volBased mode
            (this.rfqTabService.tabData as TabItem).streamedHedge =
              r.payload?.hedge || r.body?.hedge;
            if (this.isVolBased) {
              this.setPriceStreamingState('MANUAL');
            }
          },
          complete: async () => {
            this.setPriceStreamingState('UNAVAILABLE');
            this.changeRef.markForCheck();
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          },
          error: async err => {
            this.setPriceStreamingState('UNAVAILABLE');
            this.changeRef.markForCheck();
            console.error(err);
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          }
        });
    };
    resubscribe(0);
  }

  private processPricingResult(r: PriceResult) {
    if (this.priceStreamingState === 'UNAVAILABLE') {
      return;
    }

    if (r.isLastStreamingMessage) {
      console.log('Pricing Unavailable [lastStreamingMessage=true]');
      this.setPriceStreamingState('UNAVAILABLE');
    }
    this.priceResult = r;
    this.priceOffer(r.portfolioResults?.premium);
    this.priceBid(r.portfolioResults?.premium);
    this.setCashPremiums();
    this.priceSpot(r, this.quoteEvent, this.ccyPair);
    this.hasVols =
      !this.isPremiumBasedVanillasWithSpotFeed &&
      this.priceVols(r.resultPerLeg);
    this.vega = r.portfolioResults.vega; // {currency; amount }
    this.initialPrice = false;
    this.impliedMarginBid = this.calcImpliedMargin('Sell');
    this.impliedMarginOffer = this.calcImpliedMargin('Buy');

    this.form.updateValueAndValidity();
    this.changeRef.markForCheck();
    this.risks = calculateVolBasedRisks(r, this.ccyPair);
  }

  private updatePremiumInStyleValues(): void {
    if (
      !this.volBasedRfqOptionPriceResult ||
      !this.isPremiumBasedVanillasWithSpotFeed
    ) {
      return;
    }
    setVendorOptionPremium(
      this.volBasedRfqOptionPriceResult,
      this.form,
      this.premiumInStyleInfo
    );
  }

  private submitQuote(providerQuote: ProviderQuote, iter = 0) {
    const volsAboveZero = !providerQuote.legs.find(
      x =>
        (x.volatilityOffer !== undefined && +x.volatilityOffer <= 0) ||
        (x.volatilityBid !== undefined && +x.volatilityBid <= 0)
    );

    if (
      volsAboveZero &&
      (this.isVolBased || this.setupForm.valid) &&
      !this.canAccept
    ) {
      if (iter === 0) {
        this.quoteSubmit.update(s => {
          return { ...s, quoteSubmitStatus: 'SUBMITTING' };
        });
      }
      const clonedQuote = clone(providerQuote);
      const spots = this.setupForm?.value?.spots;
      if (spots) {
        clonedQuote.legs = clonedQuote.legs.map(x => {
          return { ...x, spotOffer: spots.spotAsk, spotBid: spots.spotBid };
        });
      }
      this.rfqService
        .submitProviderQuote({ providerQuote: clonedQuote })
        .subscribe({
          next: async r => {
            if (r.result === true) {
              this.quoteSubmit.set({
                firstSuccess: true,
                quoteSubmitStatus: 'SUCCESS'
              });
              this.changeRef.markForCheck();
            } else {
              console.error('submitQuote', r);
              this.changeRef.markForCheck();
              if (iter >= 5) {
                this.alertsService.addAlert({
                  message: 'Error submitting quote',
                  payload: { providerQuote: clonedQuote },
                  error: r
                });
                this.quoteSubmit.update(s => {
                  return { ...s, quoteSubmitStatus: 'ERROR' };
                });
              } else {
                await sleep(500);
                this.submitQuote(providerQuote, iter + 1);
              }
            }
          },
          error: error => {
            console.error('submitQuote', error);
            this.alertsService.addAlert({
              message: error.message,
              payload: { providerQuote: clonedQuote },
              error
            });
            this.changeRef.markForCheck();
          }
        });
    }
    return providerQuote;
  }

  private setCashPremiums(): void {
    setCashPremiums(
      this.vendorOption('bid'),
      this.vendorOption('offer'),
      this.formCtrl('cashPremiumOffer'),
      this.formCtrl('cashPremiumBid'),
      this.cashPremiumInfo,
      this.isPremiumBasedVanillasWithSpotFeed,
      this.volBasedRfqOptionPriceResult
    );
  }

  private vendorOption(side: 'bid' | 'offer'): PremiumCash {
    const hydraSide = side === 'offer' ? 'ask' : side;
    if (this.isPremiumBasedVanillasWithSpotFeed) {
      return this.premiumBasedVanillaVendorOption(hydraSide);
    }

    if (!this.priceResult) {
      return { label: '-', value: 0, direction: null };
    }
    let recieveOrPay: ReceiveOrPay;
    // XXXX fix [0]
    if (this.legInfo[0].isSimpleLeg) {
      recieveOrPay = side === 'bid' ? 'clientReceives' : 'clientPays';
    } else {
      recieveOrPay = this.formValue(`${side}Option` as any) as any;
    }

    let X =
      this.priceResult.portfolioResults?.premium?.[hydraSide]?.[
        this.premiumCCY
      ];

    if (X == null) {
      console.error(
        'Cannot get vendor premium for',
        `${side}->${hydraSide} (${this.premiumCCY})`,
        this.priceResult
      );

      return { label: '-', value: 0, direction: null };
    }
    // Need abs value (Math.abs(null)===0 so need to do it after the above test)
    X = Math.abs(X);

    const marginField = `margin${side.charAt(0).toUpperCase()}${side.slice(1)}`;
    const margin = this.numericFormValue(marginField as any) || 0;
    if (recieveOrPay === 'clientReceives') {
      const Y = X - margin;
      if (Y >= 0) {
        return {
          label: 'Client Rcvs',
          value: Math.abs(Y),
          direction: 'clientReceives'
        };
      } else {
        return {
          label: 'Client Pays',
          value: Math.abs(Y),
          direction: 'clientPays'
        };
      }
    } else {
      const Y = X + margin;
      return {
        label: 'Client Pays',
        value: Math.abs(Y),
        direction: 'clientPays'
      };
    }
  }

  private premiumBasedVanillaVendorOption(
    hydraSide: 'ask' | 'bid'
  ): PremiumCash {
    const priceResult = this.volBasedRfqOptionPriceResult;
    const premiumValue =
      priceResult.premiumValue === 'Spot' ? 'spotPremium' : 'forwardPremium';

    if (!priceResult) {
      return { label: '-', value: 0, direction: null };
    }

    const value =
      priceResult.portfolioResult[premiumValue][hydraSide][this.premiumCCY];

    if (hydraSide === 'ask') {
      return {
        label: value >= 0 ? 'Client Pays' : 'Client Rcvs',
        value: Math.abs(value),
        direction: 'clientReceives'
      };
    } else {
      return {
        label: value >= 0 ? 'Client Rcvs' : 'Client Pays',
        value: Math.abs(value),
        direction: 'clientPays'
      };
    }
  }

  private processLegs(q: ProviderQuote): ProviderQuote {
    q = clone(q);
    // XXXX fix [0]
    if (this.legInfo[0].isSimpleLeg) {
      q = this.processSimpleLeg(q);
    } else {
      q = this.processComplexLeg(q);
    }
    // TODO: We can't currently breakdown the bid/offer into the individual legs so
    // hack for now and pass the premiums into the 1st leg
    q.legs.forEach((leg, i) => {
      this.handleLegPremiumAmounts(leg, q, i);
    });
    return q;
  }

  private handleLegPremiumAmounts(
    leg: components['schemas']['QuoteLegDTO'],
    quote: ProviderQuote,
    legIndex: number
  ): void {
    const { vendorBid, vendorOffer } = this.vendorBidOffer();
    const premiumAmountValues =
      this.volBasedRfqOptionPriceResult?.results.resultPerLeg[legIndex][
        this.volBasedRfqOptionPriceResult.premiumValue === 'Spot'
          ? 'spotPremium'
          : 'forwardPremium'
      ];

    leg.premiumAmountBid = legPremiumAmountBid(
      this.isVolBased,
      this.volBasedRfqOptionPriceResult,
      this.premiumCCY,
      quote,
      legIndex,
      premiumAmountValues,
      vendorBid,
      this.cashPremiumInfo.liveValue.bid
    );

    leg.premiumAmountOffer = legPremiumAmountOffer(
      this.isVolBased,
      this.volBasedRfqOptionPriceResult,
      this.premiumCCY,
      quote,
      legIndex,
      premiumAmountValues,
      vendorOffer,
      this.cashPremiumInfo.liveValue.offer
    );
  }

  private processComplexLeg(q: ProviderQuote): ProviderQuote {
    const { vendorBid, vendorOffer } = this.vendorBidOffer();

    const bidDirection = this.cashPremiumInfo.direction.bid;

    if (bidDirection === 'clientReceives') {
      q.premiumAmountBid = +vendorBid;
    } else if (bidDirection === 'clientPays') {
      q.premiumAmountBid = -vendorBid;
    } else {
      console.error('Unknown bid option', bidDirection);
    }

    const offerDirection = this.cashPremiumInfo.direction.offer;
    if (offerDirection === 'clientPays') {
      q.premiumAmountOffer = +vendorOffer;
    } else if (offerDirection === 'clientReceives') {
      q.premiumAmountOffer = -vendorOffer;
    } else {
      console.error('Unknown offer option', offerDirection);
    }
    return q;
  }

  // Sometimes the legs are not available immediately so we need to poll for them
  // Usually only takes a few ms (1 poll)
  private async pollForLegPositions() {
    let legs: LegComponent[];
    for (let i = 0; i < 20; i++) {
      // max = 5*100 + 15*200 = 3.5s
      await sleep(i < 5 ? 100 : 200);
      legs = this.rfqTabService.selectedLegs;
      if (legs) {
        break;
      }
    }
    if (!legs) {
      console.error('Could not determine leg positions');
      return;
    }
    this.legPositions = legs.map(
      leg => leg.form.getRawValue().position || 'Unknown'
    );
  }

  private processSimpleLeg(q: ProviderQuote): ProviderQuote {
    const { vendorBid, vendorOffer } = this.vendorBidOffer();

    q.premiumAmountOffer = +vendorOffer;
    q.premiumAmountBid = +vendorBid;
    const legs = this.rfqTabService.selectedLegs;

    q.legs
      .filter((_, idx) => this.legInfo[idx].isSingleVolProduct)
      .forEach((leg, idx) => {
        const legForm = legs[idx].form.getRawValue();
        let volBid = percentageToNumber(legForm.volBid);
        let volOffer = percentageToNumber(legForm.volAsk);
        if (!isNaN(volBid)) {
          leg.volatilityBid = volBid;
        }
        if (!isNaN(volOffer)) {
          leg.volatilityOffer = volOffer;
        }
      });
    return q;
  }

  private vendorBidOffer() {
    if (this.isVolBased) {
      return { vendorBid: 0, vendorOffer: 0 };
    }

    return {
      vendorBid: cleanFormattedNumeric(this.formValue('cashPremiumBid')),
      vendorOffer: cleanFormattedNumeric(this.formValue('cashPremiumOffer'))
    };
  }

  private processHedge(q: ProviderQuote): ProviderQuote {
    if (!this.hedge) {
      return q;
    }
    q.deltaType = this.hedge.replace(/^Delta/i, '') as DeltaType;

    if (this.isVolBased) {
      q.hedgeBid = this.numericFormValue('hedgeBid');
      if (this.formCtrl('hedgeBidOption').value === 'clientBuys') {
        q.hedgeBid = -q.hedgeBid;
      }
      q.hedgeOffer = this.numericFormValue('hedgeOffer');
      if (this.formCtrl('hedgeOfferOption').value === 'clientBuys') {
        q.hedgeOffer = -q.hedgeOffer;
      }
      if (isNaN(q.hedgeOffer) || isNaN(q.hedgeBid)) {
        throw `Bad hedge params. bid:${q.hedgeBid}; offer:${q.hedgeOffer}`;
      }
      const hedgeRateBid = this.numericFormValue('hedgeRateBid');
      if (!isNaN(hedgeRateBid)) {
        q.hedgeRateBid = hedgeRateBid;
      }
      const hedgeRateOffer = this.numericFormValue('hedgeRateOffer');
      if (!isNaN(hedgeRateOffer)) {
        q.hedgeRateOffer = hedgeRateOffer;
      }
      this.currentlyQuotingClientPos = {
        bid: q.hedgeBid < 0 ? 'Client Buys' : 'Client Sells',
        offer: q.hedgeOffer < 0 ? 'Client Buys' : 'Client Sells'
      };
    } else {
      this.processNonVolBasedHedges(q);
    }
    return q;
  }

  private processNonVolBasedHedges(providerQuote: ProviderQuote): void {
    const hedge = (this.setupForm.get('hedges') as FormArray).controls[0]
      ?.value;
    if (hedge) {
      const notional = cleanFormattedNumeric(hedge.notional);
      providerQuote.hedgeRateBid = hedge.strike;
      providerQuote.hedgeRateOffer = hedge.strike;
      providerQuote.hedgeDateBid = +dayjs(hedge.settlementDate + ' GMT');
      providerQuote.hedgeDateOffer = +dayjs(hedge.settlementDate + ' GMT');
      providerQuote.hedgeBid = hedge.position === 'Buy' ? notional : -notional;
      providerQuote.hedgeOffer =
        hedge.position === 'Buy' ? -notional : notional;

      this.currentlyQuotingClientPos = {
        bid: hedge.bidPosition === 'Buy' ? 'Client Buys' : 'Client Sells',
        offer: hedge.position === 'Buy' ? 'Client Buys' : 'Client Sells'
      };
    }
  }

  private processPremiums(quote: ProviderQuote): ProviderQuote {
    const providerQuote = clone(quote);
    if (this.isPremiumBasedVanillasWithSpotFeed) {
      const providerLegs = providerQuote.legs;
      providerLegs.forEach(leg => {
        if (leg.legOptionType === 'Vanilla') {
          leg.premiumBid = leg.premiumAmountBid / leg.baseAmount;
          leg.premiumOffer = leg.premiumAmountOffer / leg.baseAmount;
        }
      });

      if (providerLegs[0].legOptionType === 'Vanilla') {
        const referenceBaseNotional = providerLegs[0].baseAmount;
        providerQuote.premiumBid =
          providerQuote.premiumAmountBid / referenceBaseNotional;
        providerQuote.premiumOffer =
          providerQuote.premiumAmountOffer / referenceBaseNotional;
      }
    }
    return providerQuote;
  }

  private attemptPickup() {
    this.rfqService
      .pickup({ providerQuote: this.quoteEvent.quote })
      .pipe(
        finalize(() => {
          this.postPickup();
          this.changeRef.markForCheck();
        })
      )
      .subscribe({
        next: r => {
          if (r['error']) {
            this.isOwner = false;
            this.handlePickupError(r);
            return;
          }
          this.isOwner = true;
        },
        error: err => {
          // should actually never get here
          this.isOwner = false;
          console.error('attemptPickup', err);
        }
      });
  }

  private postPickup() {
    this.setStatus(this.quoteEvent);
    this.setupEvent(this.quoteEvent);
    this.startExpiryTimer();
    this.startStreamingPricing();
    this.form.updateValueAndValidity();
  }

  private handlePickupError(r: any) {
    const { type, message } = r['error']?.response?.error?.exception || {};
    if (type === 'FailedPickupException') {
      this.pickupErrorMessage = null;
      // pass
    } else if (type && message) {
      this.pickupErrorMessage = `${message} [${type}]`;
    } else if (type) {
      this.pickupErrorMessage = `${type}`;
    } else {
      this.pickupErrorMessage = 'it was probably picked up by another user';
    }
  }

  private setStatus(quoteEvent: ProviderQuoteEvent) {
    this.action = quoteEvent.action;
    this.freezeQuotes();
    this.quoteEventActionChange.emit(this.action);
    this.quoteStatus = quoteEvent.quote.quoteStatus;
    this.actionMessage =
      ActionMessage[this.action] ||
      ActionMessage[this.quoteStatus] ||
      this.action ||
      this.quoteStatus ||
      'Unknown';
    this.canQuote = this.getCanQuote();

    if (!this.canQuote) {
      console.info(
        "Can't quote",
        JSON.stringify({
          canQuote: this.canQuote,
          quoteStatus: this.quoteStatus,
          action: this.action,
          isOwner: this.isOwner,
          formValid: this.formValid,
          disabled: this.initialPrice && !this.isVolBased,
          volsError: this.volsError,
          formErrors: this.form.errors
        })
      );
    }
    this.logCanQuote(quoteEvent);

    this.canWithdraw = this.action === 'ConfirmQuoteSubmit' && this.isOwner;
    this.canRelease = this.quoteStatus === 'PickedUp' && this.isOwner;
    this.canAccept =
      ['ClientAccept', 'RFEExpired'].includes(this.action) && this.isOwner;
    this.canDeclineRFQ = this.quoteStatus === 'PickedUp' && this.isOwner;

    let actions = ['ConfirmRFEAccept', 'ClientAccept'];
    this.canDeclineRFE = actions.includes(this.action) && this.isOwner;

    this.quoteInFlight = this.action === 'ConfirmQuoteSubmit';
    this.isExpired =
      this.action === 'Expired' && !DeadStatuses.includes(this.action);
    this.isComplete = DeadStatuses.includes(this.action);
    const tradedStatuses: QuoteStatus[] = ['Dealt', 'DealtAway'];
    this.isTraded = tradedStatuses.includes(this.quoteStatus);
  }

  private getCanQuote(): boolean {
    let statuses: QuoteStatus[];
    let actions: Action[];

    statuses = ['PickedUp', 'Submitted'];
    actions = ['CANCELLED'];

    return (
      statuses.includes(this.quoteStatus) &&
      !actions.includes(this.action) &&
      this.isOwner
    );
  }

  public quoteButtonDisabled(): boolean {
    return (
      !this.formValid ||
      (this.initialPrice && !this.isVolBased) ||
      !this.setupValid() ||
      this.pricesLoading
    );
  }

  /**
   * Utility function to debug quote button enable/disable
   */
  @Throttle(2000)
  private logCanQuote(quoteEvent: ProviderQuoteEvent) {
    // run in timeout so any exception doesn't effect main code
    setTimeout(() => {
      let reasons: Record<string, any> = {
        canQuote: this.canQuote,
        quoteStatus: this.quoteStatus,
        action: this.action,
        owner: quoteEvent?.quote?.owner,
        isOwner: this.isOwner,
        formValid: this.formValid,
        disabled: this.initialPrice && !this.isVolBased,
        volsError: this.volsError,
        formErrors: this.form.errors,
        form: this.form.getRawValue()
      };

      const event = {
        message: this.canQuote
          ? 'Quote Button enabled'
          : 'Quote Button disabled',
        reasons,
        quoteEvent
      };

      if (isEqual(event, this.lastPhxLogEvent)) {
        return;
      }

      if (event.message === this.lastPhxLogEvent?.message && this.canQuote) {
        return;
      }

      console.log(`${event.message} - logging to Phoenix.`);

      this.phxService.logEvent({ type: 'quote', category: 'button', ...event });
      this.lastPhxLogEvent = event;
    });
  }

  private freezeQuotes(): void {
    const freezeQuotes = ['ConfirmRFEAccept', 'ClientAccept'].includes(
      this.action
    );
    this.rfqTabService.frozenQuotes = freezeQuotes ? this.quotes : null;
  }

  private subscribeToEvents(requestId: number) {
    this.subsink.sink = this.rfqService.events$
      .pipe(
        map(events => {
          return events
            .filter(row => row['@type'] === 'ProviderQuoteEvent')
            .filter(
              (item: ProviderQuoteEvent) =>
                item.quote.request.requestId === requestId
            );
        })
      )
      .subscribe((events: ProviderQuoteEvent[]) =>
        events.forEach(evt => this.setupEvent(evt))
      );
  }

  private async subscribeToClientPrice(requestId: number, iteration = 0) {
    if (iteration > 10) {
      console.warn(
        'Subscribing to client price failed - max iterations reached'
      );
    }
    if (this.executionType == null) {
      await sleep(iteration < 5 ? 250 : 1000);
      this.subscribeToClientPrice(requestId, iteration + 1);
      return;
    }

    if (!this.isVolBased) {
      return;
    }

    console.log('Subscribing to client price', iteration);
    this.subsink.sink = this.webSocketService
      .getListener<ClientPriceResults>({ action: 'clientPrice' })
      .pipe(filter(m => m.channel === `clientPrice-${requestId}`))
      .subscribe(m => {
        const { premiumCurrency, portfolioResult, premiumStyle } = m.payload;
        if (portfolioResult.forwardPremium) {
          delete portfolioResult.forwardPremium['mid'];
        }
        if (portfolioResult.spotPremium) {
          delete portfolioResult.spotPremium['mid'];
        }
        const premiumAsk =
          portfolioResult.forwardPremium?.ask[premiumCurrency] ||
          portfolioResult.spotPremium?.ask[premiumCurrency];
        const premiumBid =
          portfolioResult.forwardPremium?.bid[premiumCurrency] ||
          portfolioResult.spotPremium?.bid[premiumCurrency];
        this.currentClientPrice = {
          premiumCurrency,
          premium: {
            ask: Math.abs(premiumAsk),
            askType: premiumAsk < 0 ? 'Receive' : 'Pay',
            bid: Math.abs(premiumBid),
            bidType: premiumBid < 0 ? 'Pay' : 'Receive'
          },
          premiumStyle
        };
      });

    this.webSocketService.sendMessage({
      action: `${API_PREFIX_V2}subscribe`,
      payload: { channels: [`clientPrice-${requestId}`] }
    });
  }

  private getNextQuoteTime() {
    const delta = this.expiresIn ? this.expiresIn - 1 : 4;
    return minmax(delta, 1, 3) * 1_000;
    // return minmax(delta, 3, 15) * 1_000;
  }

  private canRequote(): boolean {
    if (!this.activeQuote) {
      return false;
    }
    if (this.action !== 'ConfirmQuoteSubmit') {
      return false;
    }
    if (this.autoQuoteTimer == null) {
      return false;
    }
    return true;
  }

  private requote(): void {
    if (!this.enableAutoQuote) {
      return;
    }
    const delta = this.getNextQuoteTime();
    this.autoQuoteTime = Date.now() + delta;
    console.log(`Resubmitting quote in ${Math.floor(delta / 1000)}s`);

    this.autoQuoteTimer = setTimeout(() => {
      try {
        if (!this.canRequote()) {
          return;
        }
        if (
          this.quoteSubmit().quoteSubmitStatus === 'ERROR' &&
          this.enableAutoQuote
        ) {
          console.error('Cancelling requote - too many consecutive errors');
          this.cancelAutoQuoting();
          return;
        }
        if (this.enableAutoQuote) {
          this.submitQuote(this.activeQuote);
        }
      } finally {
        this.requote();
      }
    }, delta);
  }

  private cancelAutoQuoting() {
    if (this.autoQuoteTimer != null) {
      console.log('Cancelling auto quote');
      clearTimeout(this.autoQuoteTimer);
      this.autoQuoteTimer = null;
    }
    this.activeQuote = null;
  }

  private setupEvent(quoteEvent: ProviderQuoteEvent) {
    if (quoteEvent.action !== 'ConfirmQuoteSubmit') {
      this.cancelAutoQuoting();
    }

    this.setStatus(quoteEvent);
    this.quoteEvent = quoteEvent;

    if (
      this.volBasedRfqOptionPriceResult &&
      this.isPremiumBasedVanillasWithSpotFeed
    ) {
      this.premiumInStyleCurrentlyQuoted = premiumInStyleCurrentlyQuoted(
        quoteEvent,
        this.volBasedRfqOptionPriceResult
      );
    }
    const quote = this.quoteEvent.quote;
    this.timeLimit = quote.timeLimit;
    this.premiumCCY = quote.premiumCurrency;
    this.baseCCY = quote.request.quotePair.base;
    const { base, term } = quote.request.quotePair;
    this.ccyPair = `${base}${term}`;
    this.termPair = `${term}/${base}`;
    const suffix = this.isVolBased ? '-volbased' : '-normal';
    this.bidActiveClass = '';
    this.offerActiveClass = '';
    if (quote.tradeDirection === 'Sell') {
      this.bidActiveClass = `active${suffix}`;
      this.offerActiveClass = `inactive${suffix}`;
    } else if (quote.tradeDirection === 'Buy') {
      this.bidActiveClass = `inactive${suffix}`;
      this.offerActiveClass = `active${suffix}`;
    }
    this.setWLQuoteStatus();
    this.hedge = quote.request.hedge;
    if (this.hedge === 'None') {
      this.hedge = null;
    }

    this.legInfo = this.legs
      .map(leg => leg.leg.leg)
      .map((leg, i) => {
        return {
          productType: leg.productType,
          inverted: quoteEvent.quote.legs[i].inverted,
          isSingleVolProduct: SingleVolProducts.includes(leg.productType),
          isSimpleLeg: SimpleLegTypes.includes(leg.productType)
        };
      });
    this.clientActionsHeaders = this.rfqTabService.tabData.positions;
    this.setupHedgeRate(quoteEvent);
    this.impliedMarginBid = this.calcImpliedMargin('Sell');
    this.impliedMarginOffer = this.calcImpliedMargin('Buy');
    this.form.updateValueAndValidity();
    this.updateState();
    this.autoAcceptRfe();
    this.changeRef.markForCheck();
  }

  private autoAcceptRfe(): void {
    if (
      this.canAccept &&
      this.configService.userSettings().parent['trading:autoAcceptRFEs']
    ) {
      this.doAccept();
    }
  }

  private setWLQuoteStatus(): void {
    if (this.isWhiteLabel) {
      const quote = this.quoteEvent.quote;
      const selectedQuote =
        quote.tradeDirection === 'Sell'
          ? this.rfqTabService.quotesBeingDisplayed.bids.find(
              x => x.bank === this.bankToFilter
            )
          : this.rfqTabService.quotesBeingDisplayed.offers.find(
              x => x.bank === this.bankToFilter
            );

      if (!selectedQuote) {
        return;
      }

      if (
        this.quoteEvent.action === 'ClientAccept' ||
        this.quoteEvent.action === RFQ_QUOTE_CONFIRM_RFE_ACCEPT
      ) {
        selectedQuote.status = RFQ_QUOTE_CONFIRM_RFE_CLIENT_ACCEPT;
      }
    }
  }

  private setupHedgeRate(quoteEvent: ProviderQuoteEvent) {
    if (!this.hedgeRate) {
      return;
    }
    const { base, term } = quoteEvent.quote.request.quotePair;
    const dp = this.productsConfigService.decimalPlacesForCross(
      `${base}${term}`
    );
    this.formCtrl('hedgeRateBid').setValue(this.hedgeRate.toFixed(dp));
    this.formCtrl('hedgeRateOffer').setValue(this.hedgeRate.toFixed(dp));
  }

  private startExpiryTimer() {
    this.subsink.sink = interval(250).subscribe({
      next: () => {
        try {
          if (this.timeLimit != null) {
            const delta = this.timeLimit - Date.now();
            this.expiresIn = isNaN(delta) ? -1 : Math.trunc(delta / 1000);
            this.changeRef.markForCheck();
          }
          const ts = this.priceResult?.timestamp;
          if (ts) {
            this.timeSincePrice = Math.trunc(
              (Date.now() - new Date(ts).getTime()) / 1000
            );
          }
        } finally {
          this.changeRef.markForCheck();
        }
      }
    });
  }

  private priceBid(premium: Premium) {
    const bid = premium?.bid?.[this.premiumCCY];
    if (bid == null) {
      return;
    }
    this.formCtrl('premiumAmountBid').setValue(Math.abs(+bid).toFixed(2));
    const ctrl = this.formCtrl('bidOption');
    if (this.legInfo[0].isSimpleLeg) {
      ctrl.setValue('clientReceives');
    } else if (+bid > 0) {
      ctrl.setValue('clientReceives');
    } else {
      ctrl.setValue('clientPays');
    }
  }

  private priceOffer(premium: Premium) {
    const offer = premium?.ask?.[this.premiumCCY];
    if (offer == null) {
      return;
    }
    this.formCtrl('premiumAmountOffer').setValue(Math.abs(+offer).toFixed(2));
    const ctrl = this.formCtrl('offerOption');
    // XXXX fix [0]
    if (this.legInfo[0].isSimpleLeg) {
      ctrl.setValue('clientPays');
    } else if (+offer > 0) {
      ctrl.setValue('clientPays');
    } else {
      ctrl.setValue('clientReceives');
    }
  }

  private priceVols(legs: LegResult[]): boolean {
    if (!legs) {
      return false;
    }

    for (const leg of legs) {
      if (leg.volBid == null || leg.volAsk == null) {
        return false;
      }
    }

    ['volBid', 'volOffer'].forEach(key => {
      createVolsFormArray(
        key,
        legs.length,
        this.form,
        this.fb,
        this.priceStreamingState
      );
    });

    legs.forEach((leg, i) => {
      this.form.get(`volBid.${i}`).setValue((leg.volBid * 100).toFixed(4));
      this.form.get(`volOffer.${i}`).setValue((leg.volAsk * 100).toFixed(4));
    });
    return true;
  }

  private priceSpot(
    result: PriceResult,
    quoteEvent: ProviderQuoteEvent,
    ccyPair: string
  ) {
    const originalHedgeRate = quoteEvent.quote.request.hedgeRate;
    this.isCustomHedgeRate = originalHedgeRate != null;
    const dp = this.productsConfigService.decimalPlacesForCross(ccyPair);
    this.spotRate = result.marketData.spot[ccyPair]['mid'];
    this.spotDelta = result.portfolioResults.spotDelta[ccyPair];
    let delta: number;
    if (this.hedge === 'DeltaForward') {
      delta = result.portfolioResults.forwardDelta[ccyPair];
      this.hedgeRate = originalHedgeRate ?? result.resultPerLeg[0].forward; // KEEP [0] FOR NOW
    } else if (this.hedge === 'DeltaSpot') {
      delta = result.portfolioResults.spotDelta[ccyPair];
      this.hedgeRate =
        originalHedgeRate ?? result.marketData.spot[ccyPair]['mid'];
    }

    if (delta != null) {
      /*
      SPEC:
        On the bid side we should consider Y = the spot delta
        if Y >= 0 we display 'Client buys' Y
        if Y < 0 we display 'Client sells' -Y
      */
      this.formCtrl('hedgeBidOption').setValue(
        delta >= 0 ? 'clientBuys' : 'clientSells'
      );
      this.formCtrl('hedgeBid').setValue(formatNumber(delta, 0));
      /*
      SPEC:
        On the offer side we should consider X = - the spot delta
        if X >= 0 we display 'Client buys' X
        if X < 0 we display 'Client sells' -X
      */
      this.formCtrl('hedgeOfferOption').setValue(
        -delta >= 0 ? 'clientBuys' : 'clientSells'
      );
      this.formCtrl('hedgeOffer').setValue(formatNumber(delta, 0));
    }

    if (this.hedgeRate != null) {
      this.formCtrl('hedgeRateBid').setValue(this.hedgeRate.toFixed(dp));
      this.formCtrl('hedgeRateOffer').setValue(this.hedgeRate.toFixed(dp));
    }
  }

  private calcImpliedMargin(tradeDirection: 'Buy' | 'Sell'): number {
    /*
    SPEC:
      a. If the 'Currently Quoting' display is 'Client Pays X' then R = +X else R = -X
        Client Pays BID = quote.premiumAmountBid < 0
        Client Pays OFFER = quote.premiumAmountBid >= 0
      b. If the 'DV Indic' side displays 'Client Pays Y' then IM = R -Y else IM = R + Y
      c. Display IM as indicative margin in green if positive red if -ve
    */

    const getReceiveOrPayOnBasePrice = (
      direction: 'offer' | 'bid'
    ): ReceiveOrPay => {
      // XXXX fix [0]
      if (this.legInfo[0].isSimpleLeg) {
        return direction === 'bid' ? 'clientReceives' : 'clientPays';
      } else {
        if (direction === 'bid') {
          return this.formValue('bidOption') as ReceiveOrPay;
        } else {
          return this.formValue('offerOption') as ReceiveOrPay;
        }
      }
    };

    const getReceiveOrPayOnCurrentQuote = (
      direction: 'offer' | 'bid'
    ): ReceiveOrPay => {
      // XXXX fix [0]
      if (this.legInfo[0].isSimpleLeg) {
        return direction === 'bid' ? 'clientReceives' : 'clientPays';
      } else {
        if (direction === 'bid') {
          return this.quoteEvent.quote.premiumAmountBid < 0
            ? 'clientPays'
            : 'clientReceives';
        } else {
          return this.quoteEvent.quote.premiumAmountOffer >= 0
            ? 'clientPays'
            : 'clientReceives';
        }
      }
    };

    let Y: number;
    let X: number;
    let receiveOrPayOnBasePrice: ReceiveOrPay;
    let receiveOrPayOnCurrentQuote: ReceiveOrPay;
    const quote = this.quoteEvent.quote;
    if (tradeDirection === 'Sell') {
      X = Math.abs(quote.premiumAmountBid);
      Y = Math.abs(this.numericFormValue('premiumAmountBid'));
      receiveOrPayOnBasePrice = getReceiveOrPayOnBasePrice('bid');
      receiveOrPayOnCurrentQuote = getReceiveOrPayOnCurrentQuote('bid');
    } else if (tradeDirection === 'Buy') {
      X = Math.abs(quote.premiumAmountOffer);
      Y = Math.abs(this.numericFormValue('premiumAmountOffer'));
      receiveOrPayOnBasePrice = getReceiveOrPayOnBasePrice('offer');
      receiveOrPayOnCurrentQuote = getReceiveOrPayOnCurrentQuote('offer');
    }

    const R = receiveOrPayOnCurrentQuote === 'clientPays' ? X : -X;
    return receiveOrPayOnBasePrice === 'clientPays' ? R - Y : R + Y;
  }
}
